import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useFlushOnUnmount } from "#app/lib/hooks/use-flush-on-unmount";
import {
  AmountInput,
  Box,
  Button,
  Checkbox,
  cn,
  FitRow,
  Input,
  pickImageFile,
  pickImageFiles,
  TABLE_CONTROL_CLASS,
  Text,
  useIsMobile,
} from "@salestrak/ui";
import {
  AddressPropertyInput,
} from "#app/components/issue-view/address-property-input";
import { useOpenAddressOnMap } from "#app/lib/hooks/use-open-address-on-map";
import { UserSelector } from "#app/components/entity-selectors";
import {
  DeletePhotoDialog,
} from "#app/components/custom-fields/delete-photo-dialog";
import { RichTextEditor } from "#app/components/editor/rich-text-editor";
import {
  AddMediaTile,
  FIELD_MEDIA_TILE_SIZE,
  RemovableMediaTile,
  UploadingMediaTile,
} from "#app/components/media/media-tiles";
import {
  usePendingMediaUploads,
} from "#app/lib/hooks/use-pending-media-uploads";
import { stripHtmlToText } from "@salestrak/utilities";
import {
  normalizeRichTextValue,
  isRichTextEmpty,
} from "#app/lib/utils/rich-text-value";
import type { CustomFieldDef } from "./types";
import {
  OptionSelector,
  OptionSelectorTrigger,
  type OnCreateOption,
} from "./option-picker";
import type { OptionActions } from "#app/lib/hooks/use-custom-field-mutations";
import {
  OptionChip,
  OptionOverflowChip,
  OptionValueText,
  resolveSelectedOptions,
  showsOptionColors,
} from "./option-visuals";
import { dateKindOf, type DateKind } from "@salestrak/custom-fields";
import { FieldDatePicker } from "./field-date-picker";
import {
  formatDateValue,
  toDateValue,
  isMultipleField,
  compositeShellProps,
  moneyInputProps,
  numberInputProps,
  toNumericValue,
  NONE_OPTION_LABEL,
  type FieldSurface,
  type FieldControlSize,
} from "#app/lib/custom-field-utils";
import {
  CUSTOM_FIELD_MAX_LENGTHS,
  formatMoney,
  formatNumberValue,
  formatPhoneNumber,
  getDefaultValueByType,
  isValidEmailText,
  isValidUrlText,
  normalizeUrlValue,
  urlDisplayText,
} from "@salestrak/custom-fields";
import type { Omittable } from "@salestrak/ui";
import { controlVariantFor, toControlSize } from "./field-control";
import { PhoneInlineInput } from "./phone-inline-input";
import { AddressInlineInput } from "./address-inline-input";

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export type FieldRendererProps = Omittable<{
  field: CustomFieldDef;
  /** Current value (already run through dataToField → fieldValue) */
  value: unknown;
  /** Fires with the raw form value — the parent wraps this with fieldDto */
  onChangeValue: (value: unknown) => void;
  /**
   * Photos-field only. When set (view mode, issue exists), a landed upload /
   * removed tile commits as a single-photo DELTA instead of emitting the whole
   * array through `onChangeValue` — concurrent uploads then can't clobber each
   * other. Absent in create mode (no issue yet), where the renderer falls back
   * to accumulating the full array into the draft via `onChangeValue`.
   */
  onAddPhoto?: (photoId: string) => void | Promise<void>;
  onRemovePhoto?: (photoId: string) => void | Promise<void>;
  /** Whether the field is currently in its active editing state. */
  isActive?: boolean;
  /**
   * Option types only. When wired (and the viewer holds create.items and the
   * field's allowOptionCreate config permits), the value picker offers the
   * inline "Create <name>" row. Hosts without an option-create path (the
   * default-value editor, automations) simply omit it.
   */
  onCreateOption?: OnCreateOption;
  /**
   * Option types only. In-picker option management (rename/recolor/delete,
   * drag reorder) — hosts pass it only for viewers holding
   * custom_fields.manage on a persisted field.
   */
  optionActions?: OptionActions;
  /** Called when the field should exit its active editing state. */
  onDeactivate?: () => void;
  /** Autofocus the primary input when the field enters editing. */
  autoFocus?: boolean;
  /** Whether the row is currently in an invalid state (drives red border). */
  invalid?: boolean;
  /**
   * Edges of an in-place refusal: an editor that turns a keystroke down
   * (money refusing a third decimal) names why here, `null` once it clears.
   * The host paints its editing ring and label from it. The draft the editor
   * kept is still valid, so this never blocks a commit or a blur.
   */
  onDraftErrorChange?: (message: string | null) => void;
  /**
   * Visual chrome for the primary input. `"table"` (default) is the
   * borderless inline-cell look used in the issue view; `"default"` is the
   * bordered, input-tinted (bg-input/30) form-field look used in the
   * custom-field create/edit form.
   */
  surface?: FieldSurface;
  /**
   * Control height for the primary input. Omitted → each control's own default
   * (the inline/table look). Set (e.g. `"lg"` = 40px) to align with sibling
   * form controls — used by the automation condition builder.
   */
  size?: FieldControlSize;
}>;


type FieldRenderer = React.ComponentType<FieldRendererProps>;


// ---------------------------------------------------------------------------
// Text / Number / Email  (shared renderer, varies keyboardType)
// ---------------------------------------------------------------------------

function TextFieldRenderer({
  field,
  value,
  onChangeValue,
  onDeactivate,
  autoFocus,
  surface = "table",
  size,
}: FieldRendererProps) {
  const textRef = useRef(String(value ?? ""));
  // The seed the uncontrolled input opened with. The ref tracks the draft
  // after that, and a re-render (a draft-error report re-rendering the host)
  // must not hand the draft back as a changed default.
  const [initialText] = useState(textRef.current);
  const markFlushed = useFlushOnUnmount(textRef, onChangeValue);

  return (
    <Input
      defaultValue={initialText}
      onChange={(event) => {
        textRef.current = event.target.value;
      }}
      onBlur={() => {
        markFlushed();
        onChangeValue(textRef.current);
        onDeactivate?.();
      }}
      // Enter commits like blur does, as the table cell's editor does.
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
      }}
      placeholder={undefined}
      // The row's label is visible, but nothing ties it to this input.
      aria-label={field.name}
      inputMode="text"
      variant={controlVariantFor(surface)}
      size={toControlSize(size)}
      maxLength={CUSTOM_FIELD_MAX_LENGTHS[field.type]}
      autoFocus={autoFocus}
    />
  );
}

// ---------------------------------------------------------------------------
// Email
// ---------------------------------------------------------------------------

function EmailFieldRenderer({
  field,
  value,
  onChangeValue,
  onDeactivate,
  autoFocus,
  invalid,
  surface = "table",
  size,
  onDraftErrorChange,
}: FieldRendererProps) {
  const textRef = useRef(String(value ?? ""));
  // The seed the uncontrolled input opened with. The ref tracks the draft
  // after that, and a re-render (a draft-error report re-rendering the host)
  // must not hand the draft back as a changed default.
  const [initialText] = useState(textRef.current);
  const markFlushed = useFlushOnUnmount(textRef, onChangeValue);
  // The ring turns red while the draft is not yet an email and clears on the
  // keystroke that completes one ("a@d" is complete; "a@d." is not) — the
  // same predicate the commit gate reads, so a cleared ring cannot be
  // refused at blur. A draft refusal, not a rejected commit.
  const onDraftErrorChangeRef = useRef(onDraftErrorChange);
  onDraftErrorChangeRef.current = onDraftErrorChange;

  return (
    <Box fill="flex">
      <Input
        defaultValue={initialText}
        onChange={(event) => {
          const text = event.target.value;
          textRef.current = text;
          onDraftErrorChangeRef.current?.(
            !text.trim() || isValidEmailText(text) ? null : "Invalid email",
          );
        }}
        onBlur={() => {
          markFlushed();
          onChangeValue(textRef.current);
          onDeactivate?.();
        }}
        placeholder={"Email address"}
        aria-label={field.name}
        type="email"
        variant={controlVariantFor(surface)}
        size={toControlSize(size)}
        maxLength={CUSTOM_FIELD_MAX_LENGTHS.email}
        aria-invalid={invalid}
        autoFocus={autoFocus}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Paragraph
// ---------------------------------------------------------------------------

function ParagraphFieldRenderer({
  field,
  value,
  onChangeValue,
  autoFocus,
  surface = "table",
}: FieldRendererProps) {
  // Initial seed only. onUpdate fires only on real edits, so opening a legacy
  // plaintext record never dirties/persists it.
  const initialHtml = useMemo(() => normalizeRichTextValue(value), [value]);
  const htmlRef = useRef(initialHtml);
  const markFlushed = useFlushOnUnmount(htmlRef, onChangeValue);

  return (
    // RichTextEditor ships no chrome of its own, so the form surfaces wrap it
    // in the shared composite shell — chromeless (all-undefined props) on the
    // inline "table" surface. The min height keeps a bordered empty editor
    // reading as a multi-line textarea rather than a one-line input.
    <Box
      {...compositeShellProps(surface)}
      minHeight={surface === "table" ? undefined : 64}
    >
      <RichTextEditor
        label={field.name}
        compact
        mentions
        autoFocus={autoFocus}
        content={initialHtml}
        placeholder={"Write"}
        shortcutHints
        contentPaddingX="sm"
        contentPaddingY="sm"
        onUpdate={(html) => {
          htmlRef.current = html;
          markFlushed();
          onChangeValue(html);
        }}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// The option types — Single Select (`select`, one pick, solid chip) and
// Multi Select (`labels`, many picks, tinted chip cluster). Values are
// always an array of option values; single caps the array at one. Both open
// the searchable OptionSelector, which carries the gated inline-create row.
// ---------------------------------------------------------------------------

/** The value chips (or placeholder) an option field shows at rest. */
export function OptionValueChips({
  field,
  value,
  multiple,
  caret,
}: {
  field: CustomFieldDef;
  value: unknown;
  multiple: boolean;
  /** In-fill caret on the single-select chip (the chip is the trigger). */
  caret?: boolean;
}) {
  const selected = resolveSelectedOptions(field, value);
  if (selected.length === 0) {
    return (
      <Text size="sm" variant="muted">
        Select...
      </Text>
    );
  }
  // Colors off: the values read as text, not as colorless chips.
  if (!showsOptionColors(field)) {
    return <OptionValueText options={selected} caret={caret} />;
  }
  if (!multiple) {
    const first = selected[0];
    if (!first) return null;
    return (
      <OptionChip
        label={first.label}
        color={first.color}
        tone="tonal"
        caret={caret}
      />
    );
  }
  // One line, as many chips as fit, then "+N" naming the rest on hover — a
  // cell or a property row never grows a second line of chips.
  return (
    <FitRow
      renderOverflow={(hidden) => (
        <OptionOverflowChip
          names={selected.slice(selected.length - hidden).map((o) => o.label)}
        />
      )}
    >
      {selected.map((opt, index) => (
        <OptionChip
          key={`${opt.value}-${index}`}
          label={opt.label}
          color={opt.color}
          shrink
        />
      ))}
    </FitRow>
  );
}

function OptionFieldRenderer({
  field,
  value,
  onChangeValue,
  onDeactivate,
  isActive,
  surface = "table",
  size,
  onCreateOption,
  optionActions,
  multiple,
}: FieldRendererProps & { multiple: boolean }) {
  const values = useMemo(
    () =>
      Array.isArray(value)
        ? value.map(String)
        : value != null && value !== ""
          ? [String(value)]
          : [],
    [value],
  );
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isActive) {
      requestAnimationFrame(() => setOpen(true));
    }
  }, [isActive]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (!nextOpen) onDeactivate?.();
    },
    [onDeactivate],
  );

  return (
    <Box fill="flex">
      <OptionSelector
        field={field}
        values={values}
        multiple={multiple}
        onValuesChange={onChangeValue}
        open={open}
        onOpenChange={handleOpenChange}
        onCreateOption={onCreateOption}
        optionActions={optionActions}
      >
        <OptionSelectorTrigger
          render={
            <Button
              variant={
                surface === "default" || surface === "outline"
                  ? "outline"
                  : "ghost"
              }
              size={size === "lg" || size === "xl" ? "lg" : "sm"}
              justify="start"
              fullWidth
              className={cn("font-normal", surface === "table" && TABLE_CONTROL_CLASS)}
            >
              <OptionValueChips
                field={field}
                value={values}
                multiple={multiple}
                caret={!multiple}
              />
            </Button>
          }
        />
      </OptionSelector>
    </Box>
  );
}

// `select` is single BY TYPE (the legacy `multiple` flag is ignored); the
// multi twin is the `labels` type.
function SelectRenderer(props: FieldRendererProps) {
  return <OptionFieldRenderer {...props} multiple={false} />;
}

function LabelsRenderer(props: FieldRendererProps) {
  return <OptionFieldRenderer {...props} multiple />;
}

// ---------------------------------------------------------------------------
// Date (calendar day or instant, per `includeTime`) / Time
// ---------------------------------------------------------------------------

const DATE_PLACEHOLDER: Record<DateKind, string> = {
  date: "date",
  dateTime: "date & time",
  time: "time",
};

function DateFieldRenderer({
  field,
  value,
  onChangeValue,
  surface = "table",
  size,
}: FieldRendererProps) {
  const placeholder = `Select ${DATE_PLACEHOLDER[dateKindOf(field) ?? "date"]}...`;

  // The library picker owns the calendar and the typed time field, so the
  // browser's own control is never reached. On the "outline" surface the
  // picker is a form field in a column of rectangular inputs, so its trigger
  // squares to match them instead of wearing the Button's stadium default.
  return (
    <Box fill="flex">
      <FieldDatePicker
        ariaLabel={placeholder}
        field={field}
        onValueChange={onChangeValue}
        placeholder={placeholder}
        shape={surface === "outline" ? "square" : undefined}
        size={toControlSize(size)}
        value={value}
        variant={surface === "ghost" ? "ghost" : "outline"}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Money / Number
// ---------------------------------------------------------------------------

// Money and number share one editor: the value formats as it is typed and
// commits a NUMBER (or null) on blur — the library `AmountInput` owns
// grouping, the sign, the decimal cap and the in-place refusal; the field's
// config only picks the cap (and, for money, the symbol and separators).
// Money REFUSES a decimal past the currency's minor unit; number ACCEPTS it
// while typing and rounds to the field's decimal places on commit. A draft
// still open when the editor unmounts without a blur (a tab switch) is
// flushed so the last typed value is not lost.
function NumericFieldRenderer({
  field,
  value,
  onChangeValue,
  onDeactivate,
  autoFocus,
  invalid,
  surface = "table",
  size,
  onDraftErrorChange,
}: FieldRendererProps) {
  const amount = useMemo(() => toNumericValue(value), [value]);
  const draftRef = useRef<number | null>(amount);
  const flushedRef = useRef<number | null>(amount);
  const onChangeValueRef = useRef(onChangeValue);
  onChangeValueRef.current = onChangeValue;
  useEffect(
    () => () => {
      if (draftRef.current !== flushedRef.current) {
        onChangeValueRef.current(draftRef.current);
      }
    },
    [],
  );

  return (
    <AmountInput
      {...(field.type === "money"
        ? moneyInputProps(field.config?.currency)
        : numberInputProps(field.config))}
      aria-label={field.name}
      value={amount}
      onValueChange={(next) => {
        draftRef.current = next;
      }}
      onValueCommit={(next) => {
        draftRef.current = next;
        flushedRef.current = next;
        onChangeValue(next);
        onDeactivate?.();
      }}
      onRejectionChange={onDraftErrorChange}
      invalid={invalid}
      // The table cell is `bare`: the editing cell owns the chrome, so the
      // borderless recipe has nothing to strip.
      bare={surface === "table"}
      variant={surface === "table" ? undefined : controlVariantFor(surface)}
      size={toControlSize(size)}
      autoFocus={autoFocus}
    />
  );
}


// ---------------------------------------------------------------------------
// Phone  (country code picker + input)
// ---------------------------------------------------------------------------


function PhoneFieldRenderer({
  field,
  value,
  onChangeValue,
  onDeactivate,
  autoFocus,
  invalid,
  surface,
  size,
  onDraftErrorChange,
}: FieldRendererProps) {
  return (
    <PhoneInlineInput
      value={value}
      onChangeValue={onChangeValue}
      onDeactivate={onDeactivate}
      autoFocus={autoFocus}
      invalid={invalid}
      surface={surface}
      size={size}
      onDraftErrorChange={onDraftErrorChange}
      label={field.name}
    />
  );
}

// ---------------------------------------------------------------------------
// URL
// ---------------------------------------------------------------------------

function UrlFieldRenderer({
  field,
  value,
  onChangeValue,
  onDeactivate,
  autoFocus,
  invalid,
  surface = "table",
  size,
  onDraftErrorChange,
}: FieldRendererProps) {
  // The draft is the stored value verbatim, scheme included: a URL that was
  // completed to `https://…` on its last commit reopens as that URL, whole
  // and selected, so retyping replaces it and appending keeps it.
  const textRef = useRef(String(value ?? ""));
  // The seed the uncontrolled input opened with. The ref tracks the draft
  // after that, and a re-render (a draft-error report re-rendering the host)
  // must not hand the draft back as a changed default.
  const [initialText] = useState(textRef.current);
  const emitUrlValue = useCallback(
    (rawValue: string) => onChangeValue(normalizeUrlValue(rawValue)),
    [onChangeValue],
  );
  const markFlushed = useFlushOnUnmount(textRef, emitUrlValue);
  // The ring turns red while the draft is not yet a web address and clears
  // on the keystroke that completes one ("a.b" is complete; "a." is not) —
  // the same predicate the commit gate reads, so a cleared ring cannot be
  // refused at blur. A draft refusal, not a rejected commit.
  const onDraftErrorChangeRef = useRef(onDraftErrorChange);
  onDraftErrorChangeRef.current = onDraftErrorChange;

  return (
    <Box fill="flex">
      <Input
        defaultValue={initialText}
        onChange={(event) => {
          const text = event.target.value;
          textRef.current = text;
          onDraftErrorChangeRef.current?.(
            !text.trim() || isValidUrlText(text) ? null : "Invalid URL",
          );
        }}
        onFocus={(event) => {
          if (autoFocus) event.currentTarget.select();
        }}
        onBlur={() => {
          markFlushed();
          emitUrlValue(textRef.current);
          onDeactivate?.();
        }}
        placeholder={"example.com"}
        aria-label={field.name}
        type="url"
        variant={controlVariantFor(surface)}
        size={toControlSize(size)}
        maxLength={CUSTOM_FIELD_MAX_LENGTHS.url}
        aria-invalid={invalid}
        autoFocus={autoFocus}
      />
    </Box>
  );
}


// ---------------------------------------------------------------------------
// Address  (Google Places autocomplete via AddressPropertyInput)
// ---------------------------------------------------------------------------


function AddressFieldRenderer({
  field,
  value,
  onChangeValue,
  onDeactivate,
  isActive,
  surface = "table",
  size,
}: FieldRendererProps) {
  const wholeAddress =
    typeof value === "object" && value !== null && "wholeAddress" in value
      ? (value as { wholeAddress?: string }).wholeAddress
      : null;

  const handleSelect = useCallback(
    (address: string) => {
      onChangeValue({
        ...(typeof value === "object" ? value : {}),
        wholeAddress: address,
      });
      onDeactivate?.();
    },
    [value, onChangeValue, onDeactivate],
  );

  const handleClear = useCallback(() => {
    onChangeValue({});
  }, [onChangeValue]);

  const openAddressOnMap = useOpenAddressOnMap("field_renderer");
  const handleShowOnMap = useCallback(() => {
    if (wholeAddress) void openAddressOnMap({ wholeAddress });
  }, [openAddressOnMap, wholeAddress]);

  // Cell surfaces edit in place: the search input mounts inside the host's
  // editing cell, like every other single-line field. (handleSelect already
  // deactivates.)
  if (surface === "table") {
    return (
      <AddressInlineInput
        wholeAddress={wholeAddress}
        size={size}
        title={field.name}
        onSelect={handleSelect}
        onDeactivate={() => onDeactivate?.()}
      />
    );
  }

  return (
    <AddressPropertyInput
      variant={surface}
      wholeAddress={wholeAddress}
      title={field.name}
      onSelect={handleSelect}
      onClear={handleClear}
      onShowOnMap={wholeAddress ? handleShowOnMap : undefined}
      autoOpen={isActive}
      onOpenChange={(open) => {
        if (!open) onDeactivate?.();
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// SingleMember / MultiMember  (display-only for now — EntitySelector needs
// workspace user data to be wired in)
// ---------------------------------------------------------------------------

function MemberFieldRenderer({
  field,
  value,
  onChangeValue,
  onDeactivate,
  size,
}: FieldRendererProps) {
  // Cardinality is config-driven. Values are always an id array; single-mode
  // just caps the selection at one element. Member fields are members-only.
  const isSingle = !isMultipleField(field.config);
  const maxSelections = isSingle ? 1 : undefined;

  return (
    <UserSelector
      title={field.name}
      size={size}
      value={value as string | string[] | null}
      onOpenChange={(open) => {
        if (!open) onDeactivate?.();
      }}
      onValueChange={(ids) => {
        onChangeValue(ids);
        if (isSingle) onDeactivate?.();
      }}
      maxSelections={maxSelections}
      placeholder="Select member..."
    />
  );
}

// ---------------------------------------------------------------------------
// Checkbox  (boolean — toggles directly, no edit mode)
// ---------------------------------------------------------------------------

function CheckboxFieldRenderer({
  field,
  value,
  onChangeValue,
  onDeactivate,
}: FieldRendererProps) {
  return (
    <Checkbox
      aria-label={field.name}
      checked={value === true}
      onCheckedChange={(checked) => {
        onChangeValue(checked);
        onDeactivate?.();
      }}
    />
  );
}

export function isCustomFieldValueEmpty(
  field: CustomFieldDef,
  value: unknown,
): boolean {
  // Checkbox is tri-state: only `null`/`undefined` is empty. A stored
  // `false` is a deliberate "no" — keep it BEFORE the defaultVal guard
  // below, which would otherwise treat `false` as empty.
  if (field.type === "checkbox") return value == null;

  const defaultVal = getDefaultValueByType(field.type);
  if (value == null || value === defaultVal) return true;

  switch (field.type) {
    case "paragraph":
      // Stored as HTML — empty when there's no visible text.
      return isRichTextEmpty(value);
    case "select":
    case "labels":
    case "member":
    case "photos":
      return !Array.isArray(value) || value.length === 0;
    case "date":
    case "time":
      return toDateValue(value) == null;
    case "phone": {
      if (typeof value === "object" && value !== null) {
        const phone = (value as { value?: unknown }).value;
        return phone == null || String(phone).trim() === "";
      }
      return value == null || String(value).trim() === "";
    }
    case "address": {
      if (typeof value === "object" && value !== null) {
        const wholeAddress = (value as { wholeAddress?: unknown }).wholeAddress;
        return wholeAddress == null || String(wholeAddress).trim() === "";
      }
      return value == null || String(value).trim() === "";
    }
    case "url": {
      if (value == null) return true;
      return urlDisplayText(String(value)).trim() === "";
    }
    default:
      return value == null || (typeof value === "string" && value.trim() === "");
  }
}

export function getCustomFieldDisplayValue(
  field: CustomFieldDef,
  value: unknown,
): string {
  if (isCustomFieldValueEmpty(field, value)) return NONE_OPTION_LABEL;

  switch (field.type) {
    case "checkbox":
      return value ? "Yes" : "No";
    case "select":
    case "labels": {
      const selectedValues = Array.isArray(value) ? value.map(String) : [];
      const labels = selectedValues.map((selectedValue) => {
        const option = field.config?.options?.find(
          (opt) => (opt?.value ?? opt?.label ?? "") === selectedValue,
        );
        return option?.label ?? selectedValue;
      });
      return labels.join(", ");
    }
    case "date":
    case "time":
      return formatDateValue(value, dateKindOf(field));
    case "money": {
      const amount = toNumericValue(value);
      return amount === null
        ? NONE_OPTION_LABEL
        : formatMoney(amount, field.config?.currency);
    }
    case "number": {
      const amount = toNumericValue(value);
      return amount === null
        ? NONE_OPTION_LABEL
        : formatNumberValue(amount, field.config);
    }
    case "phone": {
      // NANP national / dial-code international, raw when unparseable.
      return formatPhoneNumber(value) || NONE_OPTION_LABEL;
    }
    case "address":
      if (typeof value === "object" && value !== null && "wholeAddress" in value) {
        return String((value as { wholeAddress?: unknown }).wholeAddress ?? NONE_OPTION_LABEL);
      }
      return String(value);
    case "member": {
      const count = Array.isArray(value) ? value.length : 0;
      return `${count} member${count === 1 ? "" : "s"}`;
    }
    case "photos": {
      const count = Array.isArray(value) ? value.length : 0;
      return `${count} photo${count === 1 ? "" : "s"}`;
    }
    case "url":
      return urlDisplayText(String(value));
    case "paragraph":
      return stripHtmlToText(String(value));
    default:
      return String(value);
  }
}

// ---------------------------------------------------------------------------
// Renderer map
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Photos — array of permission-gated photo ids. Tiles resolve through the
// authenticated content route; add prompts for files and uploads as media
// (linked to the issue server-side when the value persists). Single mode
// replaces the photo instead of growing the list.
// ---------------------------------------------------------------------------

function PhotosFieldRenderer({
  field,
  value,
  onChangeValue,
  onAddPhoto,
  onRemovePhoto,
  surface = "table",
}: FieldRendererProps) {
  const isMobile = useIsMobile();
  // Mobile-only: the tiny always-visible remove "×" is easy to fumble, so a tap
  // opens a confirm sheet instead of deleting. Holds the id awaiting confirm.
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const multiple = isMultipleField(field.config);
  const ids = useMemo(
    () =>
      Array.isArray(value)
        ? value.filter((v): v is string => typeof v === "string")
        : [],
    [value],
  );
  // View mode (issue exists) commits each landed photo as a single-photo delta
  // — `onAddPhoto`/`onRemovePhoto` own an atomic, commutative server write, so
  // concurrent uploads can't clobber one another. Create mode has no issue yet,
  // so it falls back to accumulating the full array into the draft. Adds are
  // async (prompt + upload); two render-time-composed adds would overwrite, so
  // the fallback composes from the latest EMITTED list, synced back whenever
  // the committed value changes.
  const delta = !!onAddPhoto && !!onRemovePhoto;
  const latestIds = useRef(ids);
  useEffect(() => {
    latestIds.current = ids;
  }, [ids]);

  const { pending, startUploads, cancel } = usePendingMediaUploads({
    onUploaded: ({ photoId }) => {
      if (delta) {
        // Single mode replaces: drop the prior photo before adding the new one.
        if (!multiple && ids[0] && ids[0] !== photoId) onRemovePhoto(ids[0]);
        onAddPhoto(photoId);
        return;
      }
      const next = multiple ? [...latestIds.current, photoId] : [photoId];
      latestIds.current = next;
      onChangeValue(next);
    },
  });

  const handleAdd = async () => {
    const files = multiple
      ? await pickImageFiles()
      : await pickImageFile().then((f) => (f ? [f] : []));
    if (!files.length) return;
    // Snapshot the photos already on the field BEFORE the batch (at click time
    // nothing is in flight, so the cache is accurate here — unlike at batch
    // end, where the React `ids` prop lags the synchronous optimistic writes).
    const base = delta && multiple ? ids : null;
    // startUploads commits in pick order and returns the committed photos in
    // that order. The per-photo delta adds race to the server (completion
    // order), so once the batch settles we rewrite the field ONCE in pick
    // order to lock it in for reloads. Single post-batch wholesale write built
    // from this authoritative list (never from the lagging cache), with a set
    // identical to what the deltas persisted — so it reorders without ever
    // dropping a photo.
    const committed = await startUploads(files);
    if (base) {
      const ordered = [...base, ...committed.map((c) => c.photoId)].filter(
        (id, i, arr) => arr.indexOf(id) === i,
      );
      onChangeValue(ordered);
    }
  };

  const handleRemove = (photoId: string) => {
    if (delta) {
      onRemovePhoto(photoId);
      return;
    }
    const next = latestIds.current.filter((id) => id !== photoId);
    latestIds.current = next;
    onChangeValue(next);
  };

  const tiles = (
    <Box
      fill="flex"
      direction="row"
      wrap
      align="center"
      gap="sm"
      paddingY="xs"
      paddingX={surface === "table" ? undefined : "xs"}
    >
      {ids.map((photoId, index) => (
        <RemovableMediaTile
          key={`${photoId}-${index}`}
          photoId={photoId}
          // Mobile confirms before deleting (easy-to-mistap × on a 32px tile);
          // desktop's hover-revealed × deletes directly.
          onRemove={isMobile ? setConfirmDeleteId : handleRemove}
          size={FIELD_MEDIA_TILE_SIZE}
        />
      ))}
      {pending.map((p) => (
        <UploadingMediaTile
          key={p.tempId}
          uri={p.uri}
          progress={p.progress}
          onCancel={() => cancel(p.tempId)}
          size={FIELD_MEDIA_TILE_SIZE}
        />
      ))}
      {/* Multiple: always allow adding more. Single: only when nothing is set
          and nothing is uploading — the progress tile stands in for Add. */}
      {multiple || (ids.length === 0 && pending.length === 0) ? (
        <AddMediaTile onClick={handleAdd} size={FIELD_MEDIA_TILE_SIZE} />
      ) : null}
    </Box>
  );

  if (!isMobile) return tiles;

  return (
    <>
      {tiles}
      <DeletePhotoDialog
        open={confirmDeleteId !== null}
        onOpenChange={(o) => {
          if (!o) setConfirmDeleteId(null);
        }}
        onConfirm={() => {
          if (confirmDeleteId) handleRemove(confirmDeleteId);
        }}
      />
    </>
  );
}

export const FIELD_RENDERERS: Record<string, FieldRenderer> = {
  text: TextFieldRenderer,
  number: NumericFieldRenderer,
  email: EmailFieldRenderer,
  paragraph: ParagraphFieldRenderer,
  select: SelectRenderer,
  labels: LabelsRenderer,
  date: DateFieldRenderer,
  dateTime: DateFieldRenderer,
  time: DateFieldRenderer,
  member: MemberFieldRenderer,
  checkbox: CheckboxFieldRenderer,
  money: NumericFieldRenderer,
  phone: PhoneFieldRenderer,
  url: UrlFieldRenderer,
  address: AddressFieldRenderer,
  photos: PhotosFieldRenderer,
};

// ---------------------------------------------------------------------------
// Convenience wrapper
// ---------------------------------------------------------------------------

export function CustomFieldRenderer({ field, ...rest }: FieldRendererProps) {
  const Renderer = FIELD_RENDERERS[field.type];
  if (!Renderer) {
    return (
      <Text size="sm" variant="muted">
        Unsupported field type
      </Text>
    );
  }
  return <Renderer field={field} {...rest} />;
}
